import axios from "axios";
import Vue from "vue";

const filterVehicle = function(state, vehicle) {
  if (state.filter.startsWith(":"))
    return vehicle.location && vehicle.location.countryCode === state.filter.substring(1).toUpperCase();
  else if (state.filter === "#servis" || state.filter === "#service")
    return vehicle.regularService === "notice" || vehicle.regularService === "warning";
  else
    return vehicle.name.toLowerCase().includes(state.filter.toLowerCase())
      || vehicle.numericPlate.toLowerCase().includes(state.filter.toLowerCase())
      || (vehicle.ride && vehicle.ride.driver && (
        vehicle.ride.driver.lastName && vehicle.ride.driver.lastName.toLowerCase().includes(state.filter.toLowerCase())
        || vehicle.ride.driver.firstName && vehicle.ride.driver.firstName.toLowerCase().includes(state.filter.toLowerCase())
      ));
};

const getCompanyVisibility = function(state, companyCode) {
  var vset = state.visibility[companyCode];

  if (vset === null || vset === undefined) {
    vset = new Set();
    state.vehicles.forEach(x => vset.add(x.vehicleId));
    state.visibility[companyCode] = vset;
  }

  return vset;
};

const visibleVehicle = function(state, vehicle, companyCode) {
  let vset = state.visibility[companyCode];

  return (vset === null || vset === undefined || vset.has(vehicle.vehicleId));
};

const filterVehicles = function(state) {
  if (state.filter === null || state.filter === undefined) {
    Vue.set(state, "filteredVehicles", state.vehicles);
  } else {
    Vue.set(state, "filteredVehicles", state.vehicles.filter(
      x => filterVehicle(state, x)
    ));
  }
};

const visibleVehicles = function(state, companyCode) {
  if (state.filter === null || state.filter === undefined) {
    Vue.set(state, "visibleVehicles", state.vehicles.filter(
      x => visibleVehicle(state, x, companyCode)
    ));
  } else {
    Vue.set(state, "visibleVehicles", state.vehicles.filter(
      x => filterVehicle(state, x) && visibleVehicle(state, x, companyCode)
    ));
  }
};

const state = {
  /**
   * Všechny načtená vozidla pro danou firmu
   * (vehicle-map)
   */
  vehicles: [],

  /**
   * Vyfiltrovaná vozidla pro danou firmu.
   */
  filteredVehicles: [],

  /**
   * Vyfiltrovaná a viditelná vozidla pro danou firmu.
   */
  visibleVehicles: [],

  /**
   * Filtr. Hledá se v názvu vozidla, SPZ a jména řidiče.
   */
  filter: null,

  /**
   * Aktuálně vybrané vozidlo
   * (vehicle)
   */
  vehicle: null,

  /**
   * Mapa (kód firmy, SET ID viditelných vozidel).
   * Po dobu přihlášení si pamatuje všehchny firmy.
   *
   * Pokud tam kód firmy neni, jsou zobrazeny všechny vozdila.
   */
  visibility: new Map(),

  /**
   * Pokud obsahuje NULL, lze provest refresh, jinak obsahuje interval handler.
   */
  refreshTimeout: null,

  fetching: false,
  fetched: false,
  running: null,

  // stavové údaje k získávání vozidla
  vehicleFetching: false,
  vehicleFetched: false,
  vehicleRunning: null,

  /**
   * Réžim mapy.
   * online, history, vehicle, logbook, refueling, events
   */
  mapMode: "online"
};

const getters = {
  getVehicles: (state) => {
    return state.vehicles;
  },
  getFilteredVehicles: (state) => {
    return state.filteredVehicles;
  },
  getVisibleVehicles: (state) => {
    return state.visibleVehicles;
  },
  isVisible: (state, getters, rootState) => (vehicle) => {
    return visibleVehicle(state, vehicle, rootState.auth.company.code);
  }
};

const actions = {
  start({ dispatch, commit }) {
    // zační vyčítat pravidelně vozidla
    dispatch("fetchVehicles");
    commit("start", setInterval(function() {
      dispatch("fetchVehicles");
    }.bind(this), 60 * 1000));
  },

  stop({ commit, state }) {
    clearInterval(state.running);

    if (state.refreshTimeout !== null)
      clearTimeout(state.refreshTimeout);

    commit("stop");
  },

  refresh({ dispatch, commit, state }, timeout = true) {
    if (state.refreshTimeout === null) {
      if (state.vehicle === null) {
        dispatch("stop");
        dispatch("start");
      } else {
        dispatch("setVehicle", state.vehicle.vehicleId);
      }

      if (timeout) {
        commit("refreshTimeout", setTimeout(function() {
          commit("refreshTimeout", null);
        }.bind(this), 15 * 1000));
      }
    }
  },

  filterVehicles({ commit, rootState }, filter) {
    commit("filterVehicles", { filter: filter, company: rootState.auth.company });
  },

  toggleVehicle({ commit, rootState }, vehicle) {
    commit("toggleVehicle", { vehicle: vehicle, company: rootState.auth.company });
  },

  showAllFilteredVehicles({ commit, rootState }) {
    commit("showAllFilteredVehicles", { company: rootState.auth.company });
  },

  hideAllFilteredVehicles({ commit, rootState }) {
    commit("hideAllFilteredVehicles", { company: rootState.auth.company });
  },

  fetchVehicles({ commit, state, rootState }) {
    // pokud se vozidla právě vyčítaji, nic nedělěj
    if (state.fetching)
      return Promise.resolve();

    const url = (rootState.groupsets.groupset !== null)
      ? "/v1/{#company#}/vehicle-map?groupset=" + rootState.groupsets.groupset.groupSetId + "&size=500"
      : "/v1/{#company#}/vehicle-map?size=500";

    // zavolame stazeni objektu, ktere az se provede, tak se objekt ulozi
    const promise = axios.get(url).then((response) => {
      commit("vehiclesFetched", { vehicles: response.data.content, company: rootState.auth.company });
      return state.vehicles;
    }).catch((error) => {
      commit("vehiclesFetched", { vehicles: [], company: rootState.auth.company });
      console.log("Vehicles fetch error", error);
      return undefined;
    });
    // nastavime, ze se objekt s touto URL uz stahuje, aby se to neptalo nekolikrat
    commit("beginFetching");
    return promise;
  },

  fetchVehicle({ commit, state }, vehicle_id) {
    // pokud se vozidla právě vyčítaji, nic nedělěj
    if (state.fetching)
      return Promise.resolve();

    const url = "/v1/{#company#}/vehicle/" + vehicle_id + "?services=1";

    // zavolame stazeni objektu, ktere az se provede, tak se objekt ulozi
    const promise = axios.get(url).then((response) => {
      commit("vehicleFetched", { vehicle: response.data });
      return state.vehicle;
    }).catch((error) => {
      commit("vehicleFetched", { vehicle: null });
      console.log("Vehicle fetch error", error);
      return undefined;
    });
    // nastavime, ze se objekt s touto URL uz stahuje, aby se to neptalo nekolikrat
    commit("beginVehicleFetching");
    return promise;
  },

  setVehicle({ commit, dispatch, state }, vehicle_id) {
    if (vehicle_id !== null) {
      if (state.running) {
        clearInterval(state.vehicleRunning);
        commit("stopVehicle", false);
      }

      let tid;

      dispatch("progress/add", "Fetch record", { root: true })
        .then(id => {
          tid = id;
          return dispatch("fetchVehicle", vehicle_id);
        }).finally(() => dispatch("progress/remove", tid, { root: true }));

      commit("startVehicle", setInterval(function() {
        dispatch("fetchVehicle", vehicle_id);
      }.bind(this), 60 * 1000));
    } else {
      clearInterval(state.vehicleRunning);

      commit("stopVehicle", true);
    }
  },

  mapMode({ commit }, mode) {
    if (mode !== "vehicle") {
      // vyresetuj zobrazení aktuálního vozdila vozidlo
      this.dispatch("online/setVehicle", null);
    }

    if (mode !== "history") {
      // vyresetuj historii
      this.dispatch("history/clear");
    }

    if (mode !== "logbook") {
      // vyresetuj knihu jizd
      this.dispatch("logbook/clear");
    }

    if (mode !== "refueling") {
      // vyresetuj tankování
      this.dispatch("refueling/clear");
    }

    if (mode !== "events") {
      // vyresetuj události
      this.dispatch("events/clear");
    }

    if (mode !== "contracts") {
      // vyresetuj zakázaky
      this.dispatch("contracts/clear");
    }

    if (mode !== "service") {
      // vyresetuj údržbu
      this.dispatch("service/clear");
    }

    commit("setMapMode", { mode: mode });
  }
};

const mutations = {
  start(state, id) {
    state.running = id;
    state.visibility = new Map();
  },

  stop(state) {
    state.running = null;
  },

  startVehicle(state, id) {
    state.vehicleRunning = id;
  },

  stopVehicle(state, reset) {
    if (reset)
      Vue.set(state, "vehicle", null);

    state.vehicleRunning = null;
  },

  refreshTimeout(state, timeout) {
    Vue.set(state, "refreshTimeout", timeout);
  },

  vehiclesFetched(state, params) {
    Vue.set(state, "vehicles", params.vehicles);
    Vue.set(state, "fetching", false);
    Vue.set(state, "fetched", true);

    filterVehicles(state);
    visibleVehicles(state, params.company.code);
  },

  filterVehicles(state, params) {
    Vue.set(state, "filter", params.filter);

    filterVehicles(state);
    visibleVehicles(state, params.company.code);
  },

  vehicleFetched(state, params) {
    Vue.set(state, "vehicle", params.vehicle);
    Vue.set(state, "vehicleFetching", false);
    Vue.set(state, "vehicleFetched", true);
  },

  showAllFilteredVehicles(state, params) {
    if (state.filter === null || state.filter === undefined)
      delete state.visibility[params.company.code];
    else {
      state.visibility[params.company.code] = new Set(
        state.vehicles.filter(x => filterVehicle(state, x)).map(x => x.vehicleId)
      );
    }

    visibleVehicles(state, params.company.code);
    Vue.set(state, "filteredVehicles", [...state.filteredVehicles]);
  },

  hideAllFilteredVehicles(state, params) {
    if (state.filter === null || state.filter === undefined) {
      delete state.visibility[params.company.code];
      state.visibility[params.company.code] = new Set();
    } else {
      let vset = getCompanyVisibility(state, params.company.code);
      state.vehicles.filter(x => filterVehicle(state, x)).forEach(x => vset.delete(x.vehicleId));
    }

    visibleVehicles(state, params.company.code);
    Vue.set(state, "filteredVehicles", [...state.filteredVehicles]);
  },

  toggleVehicle(state, params) {
    let vehicle = params.vehicle;

    if (vehicle !== null) {
      let vset = getCompanyVisibility(state, params.company.code);
      if (vset.has(vehicle.vehicleId))
        vset.delete(vehicle.vehicleId);
      else
        vset.add(vehicle.vehicleId);

      visibleVehicles(state, params.company.code);
      Vue.set(state, "filteredVehicles", [...state.filteredVehicles]);
    }
  },
  /**
   * Nastaví začátek stahování
   */
  beginFetching(state) {
    Vue.set(state, "fetching", true);
  },

  beginVehicleFetching(state) {
    Vue.set(state, "vehicleFetching", true);
  },

  setMapMode(state, params) {
    Vue.set(state, "mapMode", params.mode);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};